import React from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  label = 'items',
  maxPageButtons = 5,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const getPageRange = () => {
    const halfMaxButtons = Math.floor(maxPageButtons / 2);
    let start = Math.max(1, currentPage - halfMaxButtons);
    let end = Math.min(totalPages, start + maxPageButtons - 1);

    if (end === totalPages) {
      start = Math.max(1, end - maxPageButtons + 1);
    }

    const range = [];
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  };

  if (totalItems === 0) return null;

  return (
    <div className="flex items-center justify-start gap-4 px-6 py-2">
      <div className="flex items-center gap-4">
        <span className="dark:text-gray-400 text-textSecondary text-sm whitespace-nowrap">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} {label}
        </span>

        <div className="flex items-center space-x-2">
          <button
            className="p-2 rounded-full border border-gray-600 dark:text-gray-400 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>

          {getPageRange().map((page) => (
            <button
              key={page}
              className={`w-8 h-8 rounded-full text-sm font-medium ${
                page === currentPage
                  ? 'text-[#D9D9D9] bg-gradient-to-r from-[#FF60B6] to-[#2960FD]'
                  : 'border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200'
              }`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ))}

          {totalPages > maxPageButtons &&
            currentPage < totalPages - Math.floor(maxPageButtons / 2) && (
              <>
                <span className="text-gray-400 px-2">...</span>
                <button
                  className="w-8 h-8 rounded-full border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm"
                  onClick={() => onPageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}

          <button
            className="p-2 rounded-full border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="dark:text-gray-400 text-textSecondary text-sm whitespace-nowrap">
          Rows per page:
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="text-sm dark:bg-gray-800 bg-bgPrimary border dark:border-gray-600 border-gray-300 rounded-lg px-2 py-1 dark:text-gray-300 text-textSecondary focus:outline-none cursor-pointer"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size} className="dark:bg-[#0a1733] bg-bgPrimary">
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default Pagination;
